import React, { useCallback, useEffect, useState } from "react";
import type { DtoHistoryRecord } from "@porchpass/api-client";
import { SectionTitle, Spinner } from "@porchpass/ui";
import {
  formatHistoryDescription,
  historyActor,
} from "src/api/preApproval/historyFormatters";
import { getActivityHistory } from "src/api/preApproval";
import { PreApprovalEmptyState } from "../../components/PreApprovalEmptyState";
import { formatDate } from "../../utils";
import { useWorkspace } from "../WorkspaceContext";

function HistoryEventCard({ event }: { event: DtoHistoryRecord }) {
  return (
    <div className="rounded-xl border border-[#E4E7EC] bg-white p-3.5 flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-medium text-[#101827]">{historyActor(event)}</p>
        <span className="text-xs text-[#9AA2B2]">
          {event.created_at ? formatDate(event.created_at) : ""}
        </span>
      </div>
      <p className="text-sm text-[#697083]">
        {formatHistoryDescription(event)}
      </p>
    </div>
  );
}

export function ActivityHistoryPanel() {
  const { externalId } = useWorkspace();
  const [events, setEvents] = useState<DtoHistoryRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const nextEvents = await getActivityHistory(externalId);
      setEvents(nextEvents);
    } catch (error) {
      setEvents([]);
      setLoadError(
        error instanceof Error
          ? error.message
          : "Unable to load activity history right now.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [externalId]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  return (
    <div className="flex-1 flex flex-col gap-4 p-6 bg-[#FAFBFC] overflow-y-auto min-h-0">
      <SectionTitle
        size="md"
        title="Activity History"
        subtitle="Recent application, condition, and note activity from DigiFi."
      />

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : loadError ? (
        <PreApprovalEmptyState
          title="Unable to load activity history"
          description={loadError}
        />
      ) : events.length === 0 ? (
        <PreApprovalEmptyState
          title="No activity yet"
          description="History events will appear here as the application is updated."
        />
      ) : (
        <div className="flex flex-col gap-3">
          {events.map((event) => (
            <HistoryEventCard key={event.id} event={event} />
          ))}
        </div>
      )}
    </div>
  );
}
